import { Effect } from 'effect';
import {
  getPluginCatalogRow as findPluginCatalogRow,
  loadDescriptorCatalogItems,
  loadEcosystemCatalog,
  type CatalogPluginRow,
} from './catalog-loader.ts';

export { pluginDescriptors } from './plugin-descriptors.generated.ts';
export {
  CatalogRuntimeError,
  restoreCatalogRuntimeError,
  runCatalogSync,
} from './effect-runtime.ts';
export {
  descriptorToCatalogItem,
  descriptorToCatalogItemEffect,
  countPluginCatalogRowsBySource,
  countPluginCatalogRowsByRole,
  catalogPluginRoleTabs,
  catalogPluginSourceTabs,
  filterPluginCatalogRows,
  filterPluginCatalogRowsEffect,
  getPluginCatalogRow as findPluginCatalogRow,
  getPluginCatalogRowEffect as findPluginCatalogRowEffect,
  loadDescriptorCatalogItems,
  loadEcosystemCatalog,
  loadEcosystemCatalogEffect,
  loadLegacyPluginCatalogRows,
  loadPluginCatalogRows,
  normalizeCatalogPluginRoleFilter,
  normalizeCatalogPluginSourceFilter,
  pluginCatalogSourceBadgeLabel,
  pluginCatalogSourceDetailLabel,
  pluginCatalogTrustBadgeMeta,
  type CatalogPluginRoleFilter,
  type CatalogPluginRoleTab,
  type CatalogPluginSourceFilter,
  type CatalogPluginSourceTab,
  type CatalogPluginRow,
  type CatalogPluginRowSourceKind,
  type CatalogPluginTrustBadgeMeta,
  type CatalogPluginTrustBadgeVariant,
  type FilterPluginCatalogRowsOptions,
  type LoadedEcosystemCatalog,
  type LoadEcosystemCatalogOptions,
} from './catalog-loader.ts';
export {
  legacyDescriptorSurfaceActions,
  legacyDescriptorSurfaceKinds,
  legacyDescriptorSurfaces,
  type LegacyDescriptorSurface,
  type LegacyDescriptorSurfaceAction,
  type LegacyDescriptorSurfaceKind,
} from './legacy-surfaces.ts';

export const catalogStatuses = ['supported', 'recipe', 'planned'] as const;
export type CatalogStatus = (typeof catalogStatuses)[number];
export type CatalogStatusBadgeVariant = 'success' | 'info' | 'outline';

export interface CatalogStatusMeta {
  id: CatalogStatus;
  label: string;
  badgeVariant: CatalogStatusBadgeVariant;
}

export const catalogStatusMeta = {
  supported: { id: 'supported', label: 'Supported', badgeVariant: 'success' },
  recipe: { id: 'recipe', label: 'Recipe', badgeVariant: 'info' },
  planned: { id: 'planned', label: 'Planned', badgeVariant: 'outline' },
} as const satisfies Record<CatalogStatus, CatalogStatusMeta>;

export const catalogSections = [
  'harness',
  'parsers',
  'models',
  'integrations',
  'deploy',
  'storage',
  'observability',
] as const;
export type CatalogSection = (typeof catalogSections)[number];
export type CatalogSectionFilter = 'all' | CatalogSection;

export interface CatalogSectionMeta {
  id: CatalogSection;
  label: string;
  heading: string;
  eyebrow: string;
  body: string;
}

export interface CatalogSectionTab {
  id: CatalogSectionFilter;
  label: string;
}

export const catalogRoles = [
  'harness',
  'parser',
  'model',
  'integration',
  'source',
  'protocol',
  'deploy',
  'storage',
  'observability',
] as const;
export type CatalogRole = (typeof catalogRoles)[number];

export type CatalogPort =
  | 'Harness'
  | 'ParseDriver'
  | 'ReasoningModel'
  | 'AutomationIntegration'
  | 'SourceConnector'
  | 'ProtocolSurface'
  | 'DeployTarget'
  | 'StoragePort'
  | 'DbPort'
  | 'TelemetrySink';

export type CatalogSource =
  | {
      kind: 'descriptor';
      descriptorId: string;
      derivation: 'pending-loader' | 'derived';
    }
  | {
      kind: 'manual';
      spec: 'internal/specs/ecosystem-page.md' | 'internal/specs/okra-plugin-catalog.md';
    };

export interface CatalogItem {
  id: string;
  name: string;
  section: CatalogSection;
  status: CatalogStatus;
  role: CatalogRole;
  oneLiner: string;
  href: string;
  source: CatalogSource;
  port?: CatalogPort;
  allowedHosts?: readonly string[];
  npmPackage?: `@okrapdf/${string}`;
  packagePath?: `packages/${string}`;
  tags?: readonly string[];
}

export const catalogSectionMeta = {
  harness: {
    id: 'harness',
    label: 'Harness',
    heading: 'The okra harness',
    eyebrow: 'Native',
    body: 'The proof layer every plugin feeds: run, save, diff, eval, type, verify, cite, and render accessible HTML twins.',
  },
  parsers: {
    id: 'parsers',
    label: 'Parsers',
    heading: 'Parsers',
    eyebrow: 'ParseDriver',
    body: 'Swappable model, text-layer, and CLI paths that turn PDF bytes into bbox-cited document nodes.',
  },
  models: {
    id: 'models',
    label: 'Models',
    heading: 'Models',
    eyebrow: 'ReasoningModel',
    body: 'Reasoning providers for extraction, verification, captioning, and downstream work over the parsed graph.',
  },
  integrations: {
    id: 'integrations',
    label: 'Integrations',
    heading: 'Integrations',
    eyebrow: 'In and out',
    body: 'SaaS parsers, automation tools, source connectors, protocol surfaces, and webhooks.',
  },
  deploy: {
    id: 'deploy',
    label: 'Deploy',
    heading: 'Deploy',
    eyebrow: 'Runtime targets',
    body: 'The places okra can run while preserving the same document and plugin contracts.',
  },
  storage: {
    id: 'storage',
    label: 'Storage',
    heading: 'Storage',
    eyebrow: 'StoragePort',
    body: 'Object, local, and metadata stores behind the same repository boundary.',
  },
  observability: {
    id: 'observability',
    label: 'Observability',
    heading: 'Observability',
    eyebrow: 'TelemetrySink',
    body: 'Trace, error, and product telemetry sinks for cloud and self-hosted operators.',
  },
} as const satisfies Record<CatalogSection, CatalogSectionMeta>;

export const catalogSectionTabs = [
  { id: 'all', label: 'All' },
  ...catalogSections.map((section) => ({
    id: section,
    label: catalogSectionMeta[section].label,
  })),
] as const satisfies readonly CatalogSectionTab[];

const descriptorCatalogItems = loadDescriptorCatalogItems();

function descriptorItemsFor(section: CatalogSection): CatalogItem[] {
  return descriptorCatalogItems.filter((item) => item.section === section);
}

export const harness = [
  {
    id: 'okra-harness',
    name: 'okra harness',
    section: 'harness',
    status: 'supported',
    role: 'harness',
    port: 'Harness',
    oneLiner:
      'Run, save, diff, eval, type, verify, cite, and render accessible HTML twins over the same document graph.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    tags: ['diff', 'eval', 'verify', 'cite'],
  },
] as const satisfies readonly CatalogItem[];

export const parsers = [
  ...descriptorItemsFor('parsers'),
  {
    id: 'tesseract-poppler',
    name: 'Tesseract + Poppler',
    section: 'parsers',
    status: 'recipe',
    role: 'parser',
    port: 'ParseDriver',
    oneLiner: 'CLI-side OCR recipe for self-hosted CPU parsing when local tools are acceptable.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    tags: ['cli', 'local'],
  },
  {
    id: 'paddleocr',
    name: 'PaddleOCR',
    section: 'parsers',
    status: 'recipe',
    role: 'parser',
    port: 'ParseDriver',
    oneLiner: 'Self-hosted OCR sidecar recipe behind an okra policy envelope.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    tags: ['sidecar', 'local'],
  },
  {
    id: 'dots-ocr',
    name: 'dots.ocr',
    section: 'parsers',
    status: 'planned',
    role: 'parser',
    port: 'ParseDriver',
    oneLiner: 'Planned parser preset for demand-validated OCR/VLM experiments.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    tags: ['ocr', 'planned'],
  },
] as const satisfies readonly CatalogItem[];

export const models = [
  {
    id: 'gemini',
    name: 'Gemini',
    section: 'models',
    status: 'supported',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner:
      'Reasoning and verification model family used by current okra parser and verifier surfaces.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    allowedHosts: ['generativelanguage.googleapis.com'],
  },
  {
    id: 'anthropic-claude',
    name: 'Anthropic Claude',
    section: 'models',
    status: 'recipe',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner: 'Recipe model provider for downstream extract, verify, and caption workflows.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    allowedHosts: ['api.anthropic.com'],
  },
  {
    id: 'openai',
    name: 'OpenAI',
    section: 'models',
    status: 'recipe',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner:
      'Recipe model provider for downstream extraction and verification over parsed graphs.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    allowedHosts: ['api.openai.com'],
  },
  {
    id: 'openrouter',
    name: 'OpenRouter',
    section: 'models',
    status: 'recipe',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner: 'OpenAI-compatible routing for model-named presets such as Qwen3-VL and Mistral OCR.',
    href: '/plugins/qwen3-vl-openrouter',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    allowedHosts: ['openrouter.ai'],
  },
  {
    id: 'ollama-local',
    name: 'Ollama / local',
    section: 'models',
    status: 'recipe',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner: 'Local model recipe for self-hosted reasoning under user-controlled network policy.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'nvidia-nim',
    name: 'NVIDIA NIM',
    section: 'models',
    status: 'planned',
    role: 'model',
    port: 'ReasoningModel',
    oneLiner: 'Planned hosted/self-hosted model option for GPU-backed document workflows.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
] as const satisfies readonly CatalogItem[];

export const integrations = [
  {
    id: 'llamaparse',
    name: 'LlamaParse',
    section: 'integrations',
    status: 'recipe',
    role: 'integration',
    port: 'AutomationIntegration',
    oneLiner:
      'SaaS parser integration and flagship diff target for comparing external parse output against okra proof.',
    href: '/vs/llamaparse',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
    tags: ['saas-parser', 'diff'],
  },
  {
    id: 'n8n',
    name: 'n8n',
    section: 'integrations',
    status: 'supported',
    role: 'integration',
    port: 'AutomationIntegration',
    oneLiner: 'Automation-builder distribution path for PDF extraction and workflow templates.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'zapier',
    name: 'Zapier',
    section: 'integrations',
    status: 'recipe',
    role: 'integration',
    port: 'AutomationIntegration',
    oneLiner: 'Recipe integration for routing document events into no-code workflows.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'apify',
    name: 'Apify',
    section: 'integrations',
    status: 'supported',
    role: 'integration',
    port: 'AutomationIntegration',
    oneLiner: 'Supported crawler and workflow integration for document collection jobs.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  ...descriptorItemsFor('integrations'),
  {
    id: 'mcp-clients',
    name: 'MCP clients',
    section: 'integrations',
    status: 'supported',
    role: 'protocol',
    port: 'ProtocolSurface',
    oneLiner:
      'Claude Desktop, Codex, ChatGPT, and other MCP clients can reach okra document tools.',
    href: '/pdf-mcp',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    section: 'integrations',
    status: 'supported',
    role: 'protocol',
    port: 'ProtocolSurface',
    oneLiner: 'Push document lifecycle and workflow events into external systems.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
] as const satisfies readonly CatalogItem[];

export const deploy = [
  {
    id: 'cloudflare-workers',
    name: 'Cloudflare Workers',
    section: 'deploy',
    status: 'supported',
    role: 'deploy',
    port: 'DeployTarget',
    oneLiner:
      'First-class self-deploy target for okra-solo, Durable Objects, R2, and policy-bounded plugins.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'node',
    name: 'Node',
    section: 'deploy',
    status: 'recipe',
    role: 'deploy',
    port: 'DeployTarget',
    oneLiner: 'Recipe runtime target over the same document-runtime contracts.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'docker',
    name: 'Docker',
    section: 'deploy',
    status: 'recipe',
    role: 'deploy',
    port: 'DeployTarget',
    oneLiner: 'Container recipe target for Node/self-host and native parser sidecars.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'railway',
    name: 'Railway',
    section: 'deploy',
    status: 'supported',
    role: 'deploy',
    port: 'DeployTarget',
    oneLiner: 'Proven deploy template path for okra self-host experiments.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
] as const satisfies readonly CatalogItem[];

export const storage = [
  {
    id: 'cloudflare-r2',
    name: 'Cloudflare R2',
    section: 'storage',
    status: 'supported',
    role: 'storage',
    port: 'StoragePort',
    oneLiner:
      'Native object storage for page images, source PDFs, artifacts, and immutable snapshots.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'aws-s3',
    name: 'AWS S3',
    section: 'storage',
    status: 'recipe',
    role: 'storage',
    port: 'StoragePort',
    oneLiner: 'S3-compatible storage recipe through the shared storage port.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'local-fs',
    name: 'Local filesystem',
    section: 'storage',
    status: 'supported',
    role: 'storage',
    port: 'StoragePort',
    oneLiner: 'Local storage target for self-hosted and development workflows.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'postgres',
    name: 'Postgres',
    section: 'storage',
    status: 'recipe',
    role: 'storage',
    port: 'DbPort',
    oneLiner:
      'Node self-host document graph, PDF bytes (bytea), and leases. Object buckets wait until agents.',
    href: 'https://docs.okrapdf.com/self-host',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
] as const satisfies readonly CatalogItem[];

export const observability = [
  {
    id: 'sentry',
    name: 'Sentry',
    section: 'observability',
    status: 'supported',
    role: 'observability',
    port: 'TelemetrySink',
    oneLiner: 'Error and performance telemetry for okra app and worker surfaces.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'langfuse',
    name: 'Langfuse',
    section: 'observability',
    status: 'supported',
    role: 'observability',
    port: 'TelemetrySink',
    oneLiner: 'Trace observability for model-backed document workflows.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'posthog',
    name: 'PostHog',
    section: 'observability',
    status: 'supported',
    role: 'observability',
    port: 'TelemetrySink',
    oneLiner: 'Product analytics for funnel and activation surfaces.',
    href: 'https://docs.okrapdf.com',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
  {
    id: 'opentelemetry',
    name: 'OpenTelemetry',
    section: 'observability',
    status: 'planned',
    role: 'observability',
    port: 'TelemetrySink',
    oneLiner: 'Planned vendor-neutral tracing bridge for self-hosted and cloud deployments.',
    href: '/ecosystem',
    source: { kind: 'manual', spec: 'internal/specs/ecosystem-page.md' },
  },
] as const satisfies readonly CatalogItem[];

const authoredCatalogBySection: Record<CatalogSection, readonly CatalogItem[]> = {
  harness,
  parsers,
  models,
  integrations,
  deploy,
  storage,
  observability,
};

const loadedCatalog = loadEcosystemCatalog({
  sectionOrder: catalogSections,
  catalogBySection: authoredCatalogBySection,
});

export const catalogBySection = loadedCatalog.catalogBySection;
export const allCatalogItems = loadedCatalog.allCatalogItems;
export const catalogById = loadedCatalog.catalogById;
export const pluginCatalogRows: readonly CatalogPluginRow[] = loadedCatalog.pluginCatalogRows;
export const catalogLegacyDescriptorSurfaces = loadedCatalog.legacyDescriptorSurfaces;

function countCatalogItemsBySectionUnsafe(
  bySection: Record<CatalogSection, readonly CatalogItem[]> = catalogBySection,
): Record<CatalogSectionFilter, number> {
  return {
    all: catalogSections.reduce((total, section) => total + bySection[section].length, 0),
    harness: bySection.harness.length,
    parsers: bySection.parsers.length,
    models: bySection.models.length,
    integrations: bySection.integrations.length,
    deploy: bySection.deploy.length,
    storage: bySection.storage.length,
    observability: bySection.observability.length,
  };
}

export function countCatalogItemsBySectionEffect(
  bySection: Record<CatalogSection, readonly CatalogItem[]> = catalogBySection,
): Effect.Effect<Record<CatalogSectionFilter, number>> {
  return Effect.sync(() => countCatalogItemsBySectionUnsafe(bySection));
}

export function countCatalogItemsBySection(
  bySection: Record<CatalogSection, readonly CatalogItem[]> = catalogBySection,
): Record<CatalogSectionFilter, number> {
  return Effect.runSync(countCatalogItemsBySectionEffect(bySection));
}

function filterCatalogSectionsUnsafe(
  activeSection: CatalogSectionFilter = 'all',
): readonly CatalogSection[] {
  return activeSection === 'all' ? catalogSections : [activeSection];
}

export function filterCatalogSectionsEffect(
  activeSection: CatalogSectionFilter = 'all',
): Effect.Effect<readonly CatalogSection[]> {
  return Effect.sync(() => filterCatalogSectionsUnsafe(activeSection));
}

export function filterCatalogSections(
  activeSection: CatalogSectionFilter = 'all',
): readonly CatalogSection[] {
  return Effect.runSync(filterCatalogSectionsEffect(activeSection));
}

export function getCatalogSectionEffect(
  section: CatalogSection,
): Effect.Effect<readonly CatalogItem[]> {
  return Effect.succeed(catalogBySection[section]);
}

export function getCatalogSection(section: CatalogSection): readonly CatalogItem[] {
  return Effect.runSync(getCatalogSectionEffect(section));
}

export function getCatalogItemEffect(id: string): Effect.Effect<CatalogItem | undefined> {
  return Effect.succeed(catalogById[id]);
}

export function getCatalogItem(id: string): CatalogItem | undefined {
  return Effect.runSync(getCatalogItemEffect(id));
}

export function getPluginCatalogRowEffect(id: string): Effect.Effect<CatalogPluginRow | undefined> {
  return Effect.sync(() => findPluginCatalogRow(pluginCatalogRows, id));
}

export function getPluginCatalogRow(id: string): CatalogPluginRow | undefined {
  return Effect.runSync(getPluginCatalogRowEffect(id));
}
